import { useState, useEffect } from 'react'
import firebase from '../firebase/compat'
import { client } from './Client'
import { effectRow } from '../helpers/dashboard/buildBenchmarkRows'
import buildBenchmarkOverview from '../helpers/intelligence/buildBenchmarkOverview'

/**
 * De benchmarkrij van één MSI-effect, in dezelfde vorm als het overzicht.
 *
 * Eén aanroep in plaats van de fan-out over de hele catalogus
 * (useBenchmarkRows): het themadetail gaat over één effect, en veertig
 * aanroepen doen om er één te tonen is verspilling. Dezelfde rijopbouw
 * (`effectRow` → `buildBenchmarkOverview`), zodat 'drempel gehaald' hier
 * hetzelfde betekent als op de dashboardkaart.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * `loaded` is een eigen toestand: 'geen benchmark' is een bewering aan de
 * lezer en die mag pas gedaan worden als de read gelukt is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {{msiId: string, effectName?: string, sectors?: Array<string>}|null} effect - Het effect; null = nog niet bekend
 * @returns {{row: (object|null), positioning: (object|null), benchmarkGrowth: (number|null), loading: boolean, loaded: boolean, error: (Error|null)}}
 */
const useEffectBenchmark = (effect) => {
    const [row, setRow] = useState(/** @type {object|null} */(null))
    // De spreiding per organisatie (msiBenchmark `positioning`) en het
    // gemiddelde erbij, voor de scatterplot op het themadetail. Komt uit
    // dezelfde aanroep; de rij hierboven gooit ze weg.
    const [positioning, setPositioning] = useState(/** @type {object|null} */(null))
    const [benchmarkGrowth, setBenchmarkGrowth] = useState(/** @type {number|null} */(null))
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    const msiId = effect && typeof effect.msiId === 'string' ? effect.msiId : ''
    const effectName = (effect && effect.effectName) || ''
    // Primitieve sleutel, zodat een vers array met dezelfde sectoren de
    // aanroep niet opnieuw start.
    const sectorsKey = (effect && Array.isArray(effect.sectors) ? effect.sectors : []).join(',')

    useEffect(() => {
        if (!msiId) return undefined
        let cancelled = false
        setLoaded(false)
        setError(null)
        setRow(null)
        setPositioning(null)
        setBenchmarkGrowth(null)

        const msiBenchmark = firebase.app().functions('europe-west1').httpsCallable('msiBenchmark')
        msiBenchmark({ msiId, callerCompanyId: client })
            .then((result) => {
                if (cancelled) return
                const rij = effectRow({
                    category: '',
                    effectId: msiId,
                    effectName,
                    sectors: sectorsKey ? sectorsKey.split(',') : [],
                    benchmark: result.data || {},
                })
                setRow(buildBenchmarkOverview([rij])[0] || null)
                const data = result.data || {}
                setPositioning(data.positioning || null)
                setBenchmarkGrowth(typeof data.benchmarkGrowth === 'number' ? data.benchmarkGrowth : null)
                setLoaded(true)
            })
            .catch((err) => {
                if (cancelled) return
                // Laten zien, niet wegslikken.
                console.error('useEffectBenchmark:', err)
                setError(err)
                setRow(null)
                setLoaded(false)
            })

        return () => { cancelled = true }
    }, [msiId, effectName, sectorsKey])

    return { row, positioning, benchmarkGrowth, loading: !loaded && error === null, loaded, error }
}

export default useEffectBenchmark
